import { workflowTrackBy } from './asset.helpers';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { EmployeeAssetsService } from '../../../services/employee-assets.service';
import { AssetIssue, AssetOptions, WorkflowError } from '../../../models/employee-asset.model';
import { ASSET, ASSET_ISSUE_TYPES } from './asset.constants';
import { assetErrorKey, assetOperationId, assetToday } from './asset.helpers';

@Component({selector: 'app-asset-issue-modal', templateUrl: './asset-issue-modal.component.html',
  styleUrls: ['./asset-form.component.css']})
export class AssetIssueModalComponent implements OnInit, OnDestroy {
  trackBy = workflowTrackBy;
  employeeId: string;
  canIssue = false;
  canIssueIt = false;
  canIssueService = false;
  constants = ASSET;
  kinds: string[] = [];
  issueType = ASSET.empty;
  options: AssetOptions;
  draft: AssetIssue;
  errorKey = ASSET.empty;
  busy = false;
  loading = false;
  private submittedContent = ASSET.empty;
  private destroyed = new Subject<void>();

  constructor(public modal: NgbActiveModal, private api: EmployeeAssetsService) {}

  ngOnInit(): void {
    this.kinds = ASSET_ISSUE_TYPES.filter(kind => this.mayIssue(kind));
    this.issueType = this.kinds[ASSET.zero];
    this.draft = {operationId: assetOperationId(), employeeId: this.employeeId,
      kind: this.issueType, name: ASSET.empty,
      quantity: ASSET.one, issueDate: assetToday(), indefinite: true,
      historical: false, resourceOwnership: false};
    this.kindChanged();
    this.loadOptions();
  }

  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }

  loadOptions(): void {
    this.loading = true;
    this.api.options(this.employeeId).takeUntil(this.destroyed).subscribe(options => {
      this.options = options; this.loading = false; this.errorKey = ASSET.empty;
    }, error => { this.loading = false; this.errorKey = assetErrorKey(error, false); });
  }

  kindChanged(): void {
    this.applyIssueType();
    this.draft.issueDate = this.draft.issueDate || assetToday();
    this.draft.name = ASSET.empty; this.draft.inventoryNumber = undefined;
    this.draft.url = undefined; this.draft.login = undefined;
    this.normalizePhysical();
    this.normalizeDraft();
  }

  submit(): void {
    if (this.busy || this.loading || !this.mayIssue(this.issueType)) { return; }
    this.normalizeDraft();
    if (this.draft.kind === ASSET.service && (!this.draft.login || !this.draft.url)) { this.errorKey = ASSET.validationKey; return; }
    this.busy = true; this.errorKey = ASSET.empty;
    this.prepareOperation();
    this.saveIssue();
  }

  private mayIssue(kind: string): boolean {
    if (kind === ASSET.service) { return this.canIssueService; }
    if (kind === ASSET.itEquipment) { return this.canIssueIt; }
    return (kind === ASSET.tools || kind === ASSET.consumable) && this.canIssue;
  }

  private applyIssueType(): void {
    const physical = this.issueType === ASSET.itEquipment || this.issueType === ASSET.tools;
    this.draft.kind = physical ? ASSET.serialized : this.issueType;
    this.draft.category = physical ? this.issueType : undefined;
  }

  private normalizeDraft(): void {
    this.applyIssueType();
    this.draft.ownerId = undefined;
    if (this.draft.kind === ASSET.service) {
      this.normalizeAccess();
      return;
    }
    this.draft.serviceMode = undefined; this.draft.url = undefined; this.draft.login = undefined;
    this.draft.subscriptionId = undefined; this.draft.resourceOwnership = false;
    this.draft.indefinite = true; this.draft.dueDate = null; this.draft.model = undefined;
    if (this.draft.kind !== ASSET.serialized) {
      this.draft.inventoryNumber = undefined; this.draft.category = undefined;
    }
    if (this.draft.kind === ASSET.serialized) { this.normalizePhysical(); }
  }

  private normalizePhysical(): void {
    this.draft.quantity = ASSET.one; this.draft.ownerId = undefined;
    this.draft.model = undefined; this.draft.historical = false; this.draft.source = undefined;
  }

  private normalizeAccess(): void {
    this.draft.serviceMode = ASSET.access;
    this.draft.url = (this.draft.url || ASSET.empty).trim();
    this.draft.login = (this.draft.login || ASSET.empty).trim();
    this.draft.name = undefined; this.draft.issueDate = undefined;
    this.draft.category = undefined; this.draft.inventoryNumber = undefined; this.draft.model = undefined;
    this.draft.ownerId = undefined; this.draft.subscriptionId = undefined;
    this.draft.dueDate = null; this.draft.indefinite = true; this.draft.quantity = ASSET.one;
    this.draft.historical = false; this.draft.source = undefined; this.draft.resourceOwnership = false;
  }
  private prepareOperation(): void {
    const content = JSON.stringify(Object.assign({}, this.draft, {operationId: undefined}));
    if (this.submittedContent && this.submittedContent !== content) { this.draft.operationId = assetOperationId(); }
    this.submittedContent = content;
  }

  private saveIssue(): void {
    this.api.issue(this.draft).takeUntil(this.destroyed)
      .subscribe(result => this.modal.close(result), error => this.fail(error));
  }

  private fail(error: WorkflowError): void {
    this.busy = false; this.errorKey = assetErrorKey(error, true);
    if (this.errorKey === ASSET.deniedKey) { this.options = null; }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/employee-assets/asset-issue-modal.component.ts